import { OrderInstruction } from 'components/maps/BundleMap';
import Modal from 'components/modals/Modal';
import ChangeShelfModal from 'pages/oldPlanner/modals/ChangeShelfModal';
import ETAOptionsModal from 'pages/oldPlanner/modals/ETAOptionsModal';
import MovingOrdersToBundleModalContent, {
  onTriggerMoveOrdersToSelectedBundle,
} from 'pages/oldPlanner/planner/MovingOrdersToBundleModalContent';
import {
  calculateEtaWithAlgorithm,
  cancelBundle,
  deleteDraftBundle,
  pickupOnTop,
  pickupOnTopWithGrouping,
  publishDraft,
  reorderBundleWithAlgorithm,
  reverseDeliveries,
  splitBundleAndCreateNew,
} from 'pages/oldPlanner/utils/bundleDraft';
import React, { useState } from 'react';
import Swal from 'sweetalert2';
import {
  BundleJson,
  Instruction,
  ReorderMode,
  TravelMode,
} from 'types/bundleTypes';
import { Region } from 'types/orderTypes';

interface BundleItemBottomMenuProps {
  region: Region;
  bundle: BundleJson;
  otherBundles: BundleJson[];
  isDraft: boolean;
  splitBundleActivated: boolean;
  moveToBundleActivated: boolean;
  selectedOrders: string[];
  orderInstructions: Array<OrderInstruction>;
  isDoingSomething: boolean;
  reFetch: () => void;
  onResetActivatedFeatures: () => void;
  onStartDoingSomething: () => void;
  onStopDoingSomething: () => void;
  reorderTheNewDraft: (instructions: Instruction[]) => Promise<BundleJson>;
  saveActions: (orderInstructions: Array<OrderInstruction>) => Promise<void>;
}

export const BundleItemBottomMenu = ({
  region,
  bundle,
  orderInstructions,
  reFetch,
  onResetActivatedFeatures,
  onStartDoingSomething,
  onStopDoingSomething,
  reorderTheNewDraft,
  saveActions,
  ...props
}: BundleItemBottomMenuProps) => {
  /* This `actionInProgress` is used to make sure the button disabled immediately without delay */
  const [actionInProgress, setActionInProgress] = useState(false);

  const [shelfModal, setShelfModal] = useState(false);
  const [calculateETAModal, setCalculateETAModal] = useState(false);
  const [moveBundleModal, setMoveBundleModal] = useState(false);

  const [selectedBundleOnMoveToBundle, setSelectedBundleOnMoveToBundle] =
    useState('');

  const onDeleteClicked = async () => {
    if (!props.isDraft) {
      return;
    }

    const result = await Swal.fire({
      title: 'Danger zone!',
      text: `Are you sure you want to delete draft ${bundle.id}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete draft',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#ccc',
    });

    onStartDoingSomething();
    setActionInProgress(true);
    if (result && result.value) {
      await deleteDraftBundle(bundle.id);
      reFetch();
      setActionInProgress(false);
      onStopDoingSomething();
    }
  };

  const cancelBundleWithConfirmation = async () => {
    onStartDoingSomething();
    setActionInProgress(true);

    const result = await Swal.fire({
      title: 'Cancel bundle',
      text: `Are you sure you want to cancel bundle ${bundle.id}? This is a irreversible action. All the orders in the bundle will change their status to Ready.`, // eslint-disable-line max-len
      icon: 'warning',
      showCancelButton: true,
      focusCancel: true,
      confirmButtonText: 'Cancel bundle',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Never mind',
      cancelButtonColor: '#ccc',
    });

    if (result.value) {
      const bundleCancelled = await cancelBundle(bundle.id);
      if (bundleCancelled) {
        Swal.fire('Success!', `Bundle ${bundle.id} is cancelled`, 'success');
      } else {
        Swal.fire('Bundle could not be cancelled', undefined, 'error');
      }

      reFetch();
      setActionInProgress(false);
      onStopDoingSomething();
    }
  };

  const onCancelClicked = () => {
    if (!props.isDraft) {
      /* Cancel the bundle */
      cancelBundleWithConfirmation();
    }

    if (props.splitBundleActivated || props.moveToBundleActivated) {
      /* Cancel the SplitBundle or MoveToBundle feature */
      onResetActivatedFeatures();
    }
  };

  const pickupTopAction = async (orderInstructions: OrderInstruction[]) => {
    return await reorderTheNewDraft(
      pickupOnTop(orderInstructions).map(
        orderInstruction => orderInstruction.instruction
      )
    );
  };

  const onPickupTopClicked = async () => {
    onStartDoingSomething();
    setActionInProgress(true);

    await pickupTopAction(orderInstructions);
    reFetch();
    setActionInProgress(false);
    onStopDoingSomething();
  };

  const onOpenShelfModal = () => {
    onStartDoingSomething();
    setActionInProgress(true);
    setShelfModal(true);
  };

  const onCloseShelfModal = () => {
    setShelfModal(false);
    setActionInProgress(false);
    onStopDoingSomething();
  };

  const onOpenCalculateETAModal = () => {
    onStartDoingSomething();
    setActionInProgress(true);
    setCalculateETAModal(true);
  };

  const onCloseCalculateETAModal = () => {
    setCalculateETAModal(false);
    setActionInProgress(false);
    onStopDoingSomething();
  };

  const onTravelModeSelectedForETA = async (
    travelMode: TravelMode,
    timePerStop: number
  ) => {
    onStartDoingSomething();
    setActionInProgress(true);

    calculateEtaWithAlgorithm(
      bundle.id,
      travelMode,
      timePerStop,
      () => {},
      response => {
        Swal.fire({
          title: `Something unexpected occurred when calculating ETAs. Error response: ${JSON.stringify(
            response
          )}`,
          icon: 'error',
        }).catch(error => console.error(error.message));
      }
    ).catch(error => console.error(error.message));

    reFetch();
    onCloseCalculateETAModal();
    setActionInProgress(false);
    onStopDoingSomething();
  };

  const onReorderClicked = async () => {
    onStartDoingSomething();
    setActionInProgress(true);

    await reorderBundleWithAlgorithm(
      ReorderMode.OpenRoute,
      bundle.id,
      () => {
        /* Reorder finished/success */
        /* The `reFetch` and `setActionInProgress` should be in this scope because of the ChannelSubscription */
        reFetch();
        setActionInProgress(false);
        onStopDoingSomething();
      },
      error => {
        /* Something wrong when doing the re-order */
        Swal.fire({
          title: `Something unexpected occured when re-ordering bundle. Error response: ${JSON.stringify(
            error
          )}`,
          icon: 'error',
        });

        /* The `reFetch` and `setActionInProgress` should be in this scope because of the ChannelSubscription */
        reFetch();
        setActionInProgress(false);
        onStopDoingSomething();
      }
    );
  };

  const onReverseDeliveries = async () => {
    await saveActions(reverseDeliveries(orderInstructions));
  };

  const onPublish = async () => {
    onStartDoingSomething();
    setActionInProgress(true);

    await publishDraft(bundle.id);

    reFetch();
    setActionInProgress(false);
    onStopDoingSomething();
  };

  const publishBundleWithConfirmation = async (bundleId: string) => {
    const result = await Swal.fire({
      title: `Set status of created bundle`,
      html: `Would you like to set bundle ${bundleId} as <b>Published</b>?`,
      icon: 'info',
      showCancelButton: true,
      focusCancel: true,
      confirmButtonText: 'Yes',
      confirmButtonColor: '#00b4bb',
      cancelButtonText: 'No, leave it as Draft',
      cancelButtonColor: '#55ab6d',
    });

    if (result.value) {
      await publishDraft(bundleId);
    }
  };

  const onSplitBundle = async () => {
    onStartDoingSomething();
    setActionInProgress(true);

    /* Split Bundle API */
    let newSplitBundle: BundleJson;
    try {
      /* The `splitBundleAndCreateNew` will return the newly created bundle */
      newSplitBundle = await splitBundleAndCreateNew(
        props.selectedOrders,
        region,
        bundle.id
      );
      await pickupOnTopWithGrouping(newSplitBundle);
    } catch (error) {
      Swal.fire({
        title: `Something unexpected occured when splitting the bundle. Error response: ${error}`,
        icon: 'error',
      });
    }

    /* After the Bundle has been split */
    if (newSplitBundle) {
      /* Confirmation to Publish the newly created bundle */
      await publishBundleWithConfirmation(newSplitBundle.id);
    }

    reFetch();
    onResetActivatedFeatures();
    setActionInProgress(false);
    onStopDoingSomething();
  };

  const onOpenMoveBundleModal = () => {
    setMoveBundleModal(true);
  };
  const onCloseMoveBundleModal = () => {
    setMoveBundleModal(false);
  };

  const onSelectBundleOnMoveToBundle = (selectedBundleId: string) => {
    setSelectedBundleOnMoveToBundle(selectedBundleId);
  };

  const moveToBundleAction = async () => {
    onStartDoingSomething();
    setActionInProgress(true);

    const originBundle = bundle.id;
    const destinationBundle = selectedBundleOnMoveToBundle;

    const errorOnMoveToBundle = () => {
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: `Something went wrong when moving orders from bundle ${originBundle} to the bundle ${destinationBundle}`,
        showConfirmButton: false,
        timer: 3000,
      });
    };

    try {
      const response = await onTriggerMoveOrdersToSelectedBundle(
        originBundle,
        destinationBundle,
        null,
        props.selectedOrders
      );

      if (response) {
        Swal.fire({
          position: 'center',
          icon: 'success',
          title: `Orders moved to the bundle ${destinationBundle}`,
          showConfirmButton: false,
          timer: 2000,
        });
      } else {
        /* Unauthorized when calling MoveToBundle API will return `false` */
        errorOnMoveToBundle();
      }
    } catch (error) {
      /* Something wrong happened on the MoveToBundle API */
      errorOnMoveToBundle();
    }

    reFetch();
    onResetActivatedFeatures();
    onCloseMoveBundleModal();
    setActionInProgress(false);
    onStopDoingSomething();
  };

  const topMenuFeaturesActivated =
    props.splitBundleActivated || props.moveToBundleActivated;
  const emptySelectedOrders = props.selectedOrders.length <= 0;
  const menus: Array<{
    key: string;
    text: string;
    show: boolean;
    disabled: boolean;
    onClick?: () => void;
  }> = [
    {
      key: 'Delete-Feature',
      text: 'Delete',
      show: props.isDraft && !topMenuFeaturesActivated,
      disabled: actionInProgress || props.isDoingSomething,
      onClick: onDeleteClicked,
    },
    {
      key: 'Cancel-Feature',
      text: 'Cancel',
      show: !props.isDraft || topMenuFeaturesActivated,
      disabled: actionInProgress || props.isDoingSomething,
      onClick: onCancelClicked,
    },
    {
      key: 'Pickup-Top-Feature',
      text: 'Pickup top',
      show: !topMenuFeaturesActivated,
      disabled: actionInProgress || props.isDoingSomething,
      onClick: onPickupTopClicked,
    },
    {
      key: 'Set-Shelf-Feature',
      text: 'Set Shelf',
      show: !props.isDraft && !topMenuFeaturesActivated,
      disabled: actionInProgress || props.isDoingSomething,
      onClick: onOpenShelfModal,
    },
    {
      key: 'ETA-Calculate-Feature',
      text: 'ETA Calc.',
      show: !topMenuFeaturesActivated,
      disabled: actionInProgress || props.isDoingSomething,
      onClick: onOpenCalculateETAModal,
    },
    {
      key: 'Reorder-Feature',
      text: 'Reorder',
      show: !topMenuFeaturesActivated,
      disabled: actionInProgress || props.isDoingSomething,
      onClick: onReorderClicked,
    },
    {
      key: 'Reverse-Deliveries-Feature',
      text: 'Reverse deliveries',
      show: !topMenuFeaturesActivated,
      disabled: actionInProgress || props.isDoingSomething,
      onClick: onReverseDeliveries,
    },
    {
      key: 'Publish-Feature',
      text: 'Publish',
      show: props.isDraft && !topMenuFeaturesActivated,
      disabled: actionInProgress || props.isDoingSomething,
      onClick: onPublish,
    },
    {
      key: 'Create-New-Bundle-Feature',
      text: 'Create new bundle',
      show: props.splitBundleActivated,
      disabled:
        actionInProgress || props.isDoingSomething || emptySelectedOrders,
      onClick: onSplitBundle,
    },
    {
      key: 'Move-To-Bundle-Feature',
      text: 'Move to the bundle',
      show: props.moveToBundleActivated,
      disabled:
        actionInProgress || props.isDoingSomething || emptySelectedOrders,
      onClick: onOpenMoveBundleModal,
    },
  ];

  return (
    <>
      <div className="planner-bundle-button-group">
        {menus.map(
          menu =>
            menu.show && (
              <button
                key={menu.key}
                onClick={menu.onClick}
                disabled={menu.disabled}
              >
                {menu.text}
              </button>
            )
        )}
      </div>

      {shelfModal && (
        <ChangeShelfModal
          bundle={bundle}
          onCloseModal={onCloseShelfModal}
          onSelectModal={onCloseShelfModal}
        />
      )}

      {calculateETAModal && (
        <ETAOptionsModal
          onCloseModal={onCloseCalculateETAModal}
          onTravelModeSelected={onTravelModeSelectedForETA}
          region={bundle.region}
          predictionModel={bundle.assignedCourierPredictionModel}
        />
      )}

      {moveBundleModal && (
        <Modal
          onClose={onCloseMoveBundleModal}
          buttons={[{ title: 'Select', onClick: moveToBundleAction }]}
        >
          <MovingOrdersToBundleModalContent
            bundleList={props.otherBundles}
            selectedBundle={selectedBundleOnMoveToBundle}
            onSelectBundle={onSelectBundleOnMoveToBundle}
          />
        </Modal>
      )}
    </>
  );
};
